import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type ReprocessingInvoiceFileParams } from './types';
import { zodReprocessingInvoiceFileEndpointSchema } from './validations';

/**
 * Fetches a file referenced by an invoice requiring reprocessing from the API based on the file ID.
 * @param params - The parameters for fetching the reprocessing invoice file.
 * @returns The file content as Blob, string, or JSON object depending on the file type.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_process_file_invoice_process_file__file_id__get
 */
export const fetchReprocessingInvoiceFile = async ({
  fileId,
  fileType,
  processId,
}: ReprocessingInvoiceFileParams): Promise<
  Blob | string | Record<string, unknown>
> => {
  try {
    const response = await vestigasApi.get(
      ENDPOINT.INVOICE_REPROCESSING.GET_FILE(processId, fileId),
      { validate: { pathSegments: zodReprocessingInvoiceFileEndpointSchema } },
    );

    switch (fileType) {
      case 'application/json': {
        return await response.json();
      }

      case 'image/gif':
      case 'image/jpeg':
      case 'application/pdf':
      case 'image/png': {
        return await response.blob();
      }

      case 'application/xml':
      default: {
        if (fileType?.startsWith('application/pdf')) {
          return await response.blob();
        }

        return await response.text();
      }
    }
  } catch (error) {
    log.error({
      message: 'Error fetching reprocessing invoice file',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.fetchReprocessingInvoiceFile.error404,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.fetchReprocessingInvoiceFile.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
